/**
 * Start or complete a booking without its code (doc 04-backend/31 §8): the dead-phone case.
 *
 * **Built from the spec, not a design.** No design frame exists for it; the owner ruled on
 * 2026-10-26 that design-less screens are built from the docs with the console's own components
 * and restyled later.
 *
 * Offered only in the one state each route accepts — `provider_en_route` for the start,
 * `in_progress` for the completion — so the button never leads to a 409. The server takes a
 * reason of at least ten characters and a ticket of three to twelve: the dialog holds the confirm
 * shut until the ticket fits, and the reason length is the server's to refuse, verbatim.
 */
import { useState } from 'react';
import { ConfirmAction, ScopeAction } from '../components/kit';
import { Input } from '../components/forms';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useCodeOverride, type CodeOverrideKind } from '../lib/api/hooks/bookings';

const KIND_FOR: Record<string, CodeOverrideKind> = { provider_en_route: 'start', in_progress: 'completion' };

/** Which override a booking in this state can take, or `null` when neither route would accept it. */
export function overrideFor(status: string): CodeOverrideKind | null {
  return KIND_FOR[status] ?? null;
}

export function ticketProblem(ticket: string): string | null {
  const t = ticket.trim();
  if (t.length < 3 || t.length > 12) return 'a support ticket code, 3 to 12 characters';
  return null;
}

const COPY: Record<CodeOverrideKind, { button: string; title: string; done: string; consequences: string[] }> = {
  start: {
    button: 'Start without the code',
    title: 'Start this ceremony without the start code?',
    done: 'Started by override — recorded against the ticket.',
    consequences: [
      'Only for a devotee who cannot read the start code — a dead phone, no signal. Not for a pandit who has not arrived.',
      'The booking moves to In progress now. The override, your name, the reason and the ticket are kept in the audit log and the booking’s trail, permanently.',
    ],
  },
  completion: {
    button: 'Complete without the code',
    title: 'Complete this ceremony without the completion code?',
    done: 'Completed by override — the issue window is open.',
    consequences: [
      'You are deciding the puja happened without the code that proves it. Do this only on a ticket where the devotee has said so.',
      'It is not a payout decision: the pandit’s money still waits for the issue window and the payout run.',
      'Recorded against your name with the reason and the ticket, permanently.',
    ],
  },
};

export function CodeOverrideAction({ code, status }: { code: string; status: string }) {
  const { scope, toast } = useConsole();
  const [open, setOpen] = useState(false);
  const [ticket, setTicket] = useState('');
  const override = useCodeOverride();
  const kind = overrideFor(status);
  if (kind === null) return null;
  const copy = COPY[kind];
  const problem = ticketProblem(ticket);

  return (
    <>
      <ScopeAction scope={scope} need="support" size="sm" variant="secondary" onClick={() => setOpen(true)}>
        {copy.button}
      </ScopeAction>
      {open && (
        <ConfirmAction
          open
          title={copy.title}
          phrase={code}
          tone="error"
          reason="Why the code could not be used (at least 10 characters)"
          consequences={copy.consequences}
          blocked={problem ? [problem] : []}
          confirmLabel={copy.button}
          onCancel={() => setOpen(false)}
          onConfirm={(reason) =>
            override.mutate(
              { code, kind, reason, ticket_code: ticket.trim() },
              {
                onSuccess: () => {
                  setOpen(false);
                  setTicket('');
                  toast({ tone: 'success', message: copy.done });
                },
                // The server's refusal, verbatim: a short reason or a moved booking is its call.
                onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
              },
            )
          }
        >
          <Input label="Support ticket" value={ticket} onChange={setTicket} placeholder="e.g. TKT48213" />
        </ConfirmAction>
      )}
    </>
  );
}
